import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Input from "../../components/controls/input/customInput/CustomInput";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import UserSelect from "../../components/userSelect/UserSelect";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import Table from "../../components/controls/tables/Table";
import {
    getRunbooksRequest,
    getRunbookDetailRequest,
    createRunbookRequest,
    updateRunbookRequest,
    deleteRunbookRequest,
} from "../../store/actions/runbooks.actions";
import {
    allRunbooks,
    runbooksPagination,
    runbooksPageLoading,
    runbookLoading,
    runbookDetail,
    runbookDetailLoading,
} from "../../store/selectors/runbooks.selector";
import {
    RunbookStatus,
    getRunbookStatusLabel,
    formatDuration,
    UserSearchRefType,
    calculatePageSize,
} from "../../helpers/utils";
import { use } from "react";
import InputUnderline from "../../components/controls/input/InputUnderline";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import runbooksService from "../../store/services/runbooks.service";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import { calcDurationMinutes, STATUS_OPTIONS, INITIAL_FORM } from "./utils";

const Runbooks = ({ projectID }) => {
    const dispatch = useDispatch();

    const runbooks = useSelector(allRunbooks) || [];
    const pagination = useSelector(runbooksPagination);
    const pageLoading = useSelector(runbooksPageLoading);
    const saving = useSelector(runbookLoading);
    const runbookItem = useSelector(runbookDetail);
    const runbookItemLoading = useSelector(runbookDetailLoading);

    const [pageNum, setPageNum] = useState(1);
    const [perPageItems] = useState(calculatePageSize());
    const [tableSearch, setTableSearch] = useState("");
    const [data, setData] = useState([]);
    const [isCreate, setIsCreate] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [form, setForm] = useState(INITIAL_FORM);
    const [plannedDuration, setPlannedDuration] = useState(null);
    const [actualDuration, setActualDuration] = useState(null);

    useEffect(() => {
        setPlannedDuration(calcDurationMinutes(form.plannedStartDate, form.plannedEndDate));
    }, [form.plannedStartDate, form.plannedEndDate]);

    useEffect(() => {
        setActualDuration(calcDurationMinutes(form.actualStartDate, form.actualEndDate));
    }, [form.actualStartDate, form.actualEndDate]);

    // ── Data fetching ──────────────────────────────────────────────────────────

    const fetchData = useCallback(
        (page = pageNum, search = tableSearch) => {
            if (!projectID) return;
            dispatch(
                getRunbooksRequest({
                    projectId: projectID,
                    searchTerm: search || undefined,
                    page,
                    size: perPageItems,
                })
            );
        },
        [projectID, pageNum, tableSearch, perPageItems, dispatch]
    );

    useEffect(() => {
        setData([]);
        setPageNum(1);
        fetchData(1, tableSearch);
    }, [projectID]);

    useEffect(() => {
        if (runbooks && runbooks.length > 0) {
            if (pageNum === 1) {
                setData(runbooks);
            } else {
                setData((prev) => {
                    const ids = new Set(prev.map((r) => r.id));
                    return [...prev, ...runbooks.filter((r) => !ids.has(r.id))];
                });
            }
        } else if (runbooks && runbooks.length === 0 && pageNum === 1) {
            setData([]);
        }
    }, [runbooks]);

    // ── Form population when editing ──────────────────────────────────────────

    useEffect(() => {
        setForm(INITIAL_FORM);
        setFormErrors({});
        if (isEdit && selectedRow?.id) {
            dispatch(getRunbookDetailRequest({ id: selectedRow.id }));
        }
    }, [isEdit, selectedRow]);

    useEffect(() => {
        if (isEdit && runbookItem && runbookItem.id === selectedRow.id) {
            setForm({
                name: runbookItem.name || "",
                refId: runbookItem.refId || "",
                description: runbookItem.description,
                owner: runbookItem.owner || null,
                plannedStartDate: runbookItem.plannedStartDate,
                plannedEndDate: runbookItem.plannedEndDate,
                actualStartDate: runbookItem.actualStartDate,
                actualEndDate: runbookItem.actualEndDate,
                assignees: runbookItem.assignees || [],
                status: runbookItem.status ?? RunbookStatus.Draft,
                attachments: runbookItem.attachments || [],
            });
        }
    }, [runbookItem, isEdit, selectedRow]);

    // ── Handlers ───────────────────────────────────────────────────────────────

    const update = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleSearchTrigger = (term) => {
        if ((!tableSearch && !term) || tableSearch === term) return;
        setData([]);
        setPageNum(1);
        setTableSearch(term);
        fetchData(1, term);
    };

    const handleCreate = (val) => {
        setIsCreate(val);
        setSelectedRow({});
        setForm(INITIAL_FORM);
        setFormErrors({});
    };

    const handleCancel = () => {
        setIsEdit(false);
        setFormErrors({});
        setForm(INITIAL_FORM);
    };

    const handleDelete = (row) => {
        dispatch(deleteRunbookRequest({ id: row.id, projectId: projectID }));
    };

    const handleSave = () => {
        const errors = {};
        if (!form.name?.trim()) errors.name = "Name is required";
        setFormErrors(errors);
        if (Object.keys(errors).length) return;

        const showActual = isEdit && form.status >= RunbookStatus.InProgress;

        const payload = {
            name: form.name.trim(),
            refId: form.refId?.trim(),
            description: form.description || "",
            ownerId: form.owner?.id,
            plannedStartDate: form.plannedStartDate,
            plannedEndDate: form.plannedEndDate,
            actualStartDate: showActual ? form.actualStartDate : null,
            actualEndDate: showActual ? form.actualEndDate : null,
            assigneeIds: form.assignees ? form.assignees.map((a) => a.id) : [],
        };

        if (isEdit) {
            dispatch(
                updateRunbookRequest({
                    id: runbookItem.id,
                    projectId: projectID,
                    payload: { ...payload, status: form.status },
                })
            );
        } else {
            dispatch(
                createRunbookRequest({
                    projectId: projectID,
                    payload: { ...payload, projectId: projectID },
                })
            );
        }
    };

    // ── Form body ──────────────────────────────────────────────────────────────

    const showActual = isEdit && form.status >= RunbookStatus.InProgress;


    const formBody = () => (
        <div className="d-flex flex-column gap-3">
            <div className="row gx-3 align-items-end">
                <div className={"col-9"}>
                    <InputUnderline
                        placeholder="Enter name"
                        value={form.name}
                        maxLength={255}
                        handleChange={(v) => update("name", v)}
                        error={formErrors.name}
                        isRequired
                        loading={runbookItemLoading}
                    />
                </div>
                <div className="col-3">
                    <UserSelect
                        refType={UserSearchRefType.Project}
                        refId={projectID}
                        value={form.owner}
                        label="Owner"
                        buttonLabel={"Select Owner"}
                        onChange={(val) => {
                            update("owner", val || null);
                        }}
                        loading={runbookItemLoading}
                    />
                </div>
            </div>
            <div className="row gx-3 align-items-end">
                <div className="col-4">
                    <Input
                        icon={false}
                        placeholder="Enter Change Ticket"
                        value={form.refId}
                        maxLength={255}
                        type="text"
                        handleChange={(v) => update("refId", v)}
                        label="Change Ticket"
                        loading={runbookItemLoading}
                    />
                </div>
                {
                    isEdit && (
                        <div className="col-4">
                            <DropDown
                                label="Status"
                                options={STATUS_OPTIONS}
                                defaultVal={getRunbookStatusLabel(form.status)}
                                onChange={(val) => update("status", val)}
                                loading={runbookItemLoading}
                            />
                        </div>
                    )
                }
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <UserSelect
                        multiple={true}
                        refType={UserSearchRefType.Project}
                        refId={projectID}
                        value={form.assignees}
                        label="Assignees"
                        buttonLabel={"Select Assignees"}
                        onChange={(selected) => {
                            update("assignees", selected || []);
                        }}
                        loading={runbookItemLoading}
                    />
                </div>
            </div>
            <div className="row gx-3 align-items-end">
                <div className="col">
                    <Input
                        label="Planned Start"
                        value={form.plannedStartDate}
                        type="datetime"
                        handleChange={(v) => { console.log(v); update("plannedStartDate", v); }}
                        maxDate={form.plannedEndDate}
                        loading={runbookItemLoading}
                    />

                </div>
                <div className="col">
                    <Input
                        label="Planned End"
                        value={form.plannedEndDate}
                        type="datetime"
                        handleChange={(v) => update("plannedEndDate", v)}
                        minDate={form.plannedStartDate}
                        loading={runbookItemLoading}
                    />

                </div>
                <div className="col-2">
                    <label className="Input_label">Duration</label>
                    <p
                        style={{ border: "1px solid #342C82" }}
                        className="flex font-figtree justify-center items-center text-xs text-[#1F1F1F] font-semibold bg-[#EDECF8] text-center py-[4px] h-[40px] rounded-md"
                    >
                        {plannedDuration !== null ? formatDuration(plannedDuration) : "-"}
                    </p>
                </div>
            </div>

            {showActual && (
                <div className="row gx-3 align-items-end">
                    <div className="col">
                        <Input
                            label="Actual Start"
                            value={form.actualStartDate}
                            type="datetime"
                            handleChange={(v) => update("actualStartDate", v)}
                            maxDate={form.actualEndDate}
                            loading={runbookItemLoading}
                        />

                    </div>
                    <div className="col">
                        <Input
                            label="Actual End"
                            value={form.actualEndDate}
                            type="datetime"
                            handleChange={(v) => update("actualEndDate", v)}
                            minDate={form.actualStartDate}
                            loading={runbookItemLoading}
                        />

                    </div>
                    <div className="col-2">
                        <label className="Input_label">Duration</label>
                        <p
                            style={{ border: "1px solid #342C82" }}
                            className="flex font-figtree justify-center items-center text-xs text-[#1F1F1F] font-semibold bg-[#EDECF8] text-center py-[4px] h-[40px] rounded-md"
                        >
                            {actualDuration ? formatDuration(actualDuration) : "-"}
                        </p>
                    </div>
                </div>
            )}
            <div className="row gx-3">
                <div className="col-12">
                    <RichTextEditor
                        label="Description"
                        content={form.description}
                        onChange={(v) => update("description", v)}
                        loading={runbookItemLoading}
                    />
                </div>
            </div>
            <div className="row gx-3">
                <div className="col-12">
                    <EntityFileUpload
                        entityId={selectedRow?.id}
                        entityType="Runbook"
                        attachments={form.attachments}
                        onAttachmentsChange={() => {
                            if (selectedRow?.id) {
                                runbooksService.getRunbookDetailRequest(selectedRow?.id)
                                    .then((runbookData) => {
                                        update("attachments", runbookData.attachments);
                                    })
                                    .catch((error) => {
                                        console.error("Error fetching runbook data:", error);
                                    });
                            }
                        }}
                        disabled={runbookItemLoading || !selectedRow.id}
                    />
                </div>
            </div>
        </div>
    );

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <ColumnConfigProvider tableName={"Runbook"}>
            <div className="listingScreen position-relative">
                <Table
                    type="Runbook"
                    rows={data || []}
                    body={formBody()}
                    searchHandler={handleSearchTrigger}
                    saveBtnLabel="Save"
                    cancelBtnLabel="Cancel"
                    setPageNum={setPageNum}
                    perPageItems={perPageItems}
                    setIsCreate={handleCreate}
                    loading={pageLoading}
                    modalLoading={saving}
                    isCreate={isCreate}
                    pagination={pagination}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    handleCancel={handleCancel}
                    setIsEdit={setIsEdit}
                    setSelectedRow={setSelectedRow}
                    projectId={projectID}
                    parentType="Project"
                    handleAddRow={() => { }}
                    handleCancelAddRow={() => { }}
                    setData={setData}
                    triggerSearch={handleSearchTrigger}
                />
            </div>
        </ColumnConfigProvider>
    );
};

export default Runbooks;

